import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { acknowledgeMorningDigest, fetchMorningDigest } from '../../services/morningDigestService';
import {
  formatTodayDigestLabel,
  greetingForLocalHour,
  hasDigestComeTodayIst,
  isNightOwlHour,
  isAwayForDigest,
  markDigestShownTodayIst,
  msUntilTenAmIst,
  readDigestHideNews,
  shouldAutoOpenDigest,
  touchLastActiveAt,
  writeDigestHideNews,
} from '../../utils/digestWelcomePrefs';

const CONFETTI_COLORS = ['#f43f5e', '#f59e0b', '#38bdf8', '#a855f7', '#22c55e', '#fb7185', '#fde047'];

const buildCrackerBurst = (originX, originY, seed) =>
  Array.from({ length: 28 }, (_, i) => {
    const angle = (seed + i * 13) % 360;
    const dist = 90 + ((seed + i * 17) % 160);
    const rad = (angle * Math.PI) / 180;
    return {
      id: `${seed}-${i}`,
      tx: Math.cos(rad) * dist,
      ty: Math.sin(rad) * dist - 40,
      rot: (i % 2 === 0 ? 1 : -1) * (120 + (i * 19) % 200),
      size: 6 + (i % 7),
      delay: 0.05 + (i % 10) * 0.04,
      duration: 1.15 + (i % 5) * 0.12,
      shape: i % 3,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      left: originX,
      top: originY,
    };
  });

const CrackerIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="none" aria-hidden="true">
    <path d="M38 42c-7 7-18 9-24 5 6-2 12-10 16-18 4-8 4-18 1-24 8 4 14 14 7 37z" fill="#f43f5e" />
    <path d="M31 29c4 8 10 16 16 18-6 4-17 2-24-5 3-5 6-9 8-13z" fill="#fb7185" />
    <path d="M42 10l4 8 8-1-6 6 4 8-8-4-6 6 1-8-6-6 8 1 5-10z" fill="#f59e0b" />
    <circle cx="52" cy="14" r="3" fill="#38bdf8" />
    <circle cx="58" cy="24" r="2.2" fill="#a855f7" />
    <circle cx="46" cy="6" r="2" fill="#22c55e" />
  </svg>
);

const openNewsLink = async (url) => {
  const href = String(url || '').trim();
  if (!href) return;
  try {
    if (window.workloopRealm?.openExternal) {
      await window.workloopRealm.openExternal(href);
      return;
    }
  } catch {
    // fall through to browser tab
  }
  window.open(href, '_blank', 'noopener,noreferrer');
};

const joinList = (items, labelFor) => {
  if (!items.length) return '';
  if (items.length === 1) return labelFor(items[0]);
  if (items.length === 2) return `${labelFor(items[0])} and ${labelFor(items[1])}`;
  return `${items.slice(0, -1).map(labelFor).join(', ')}, and ${labelFor(items[items.length - 1])}`;
};

const MorningDigestPopup = () => {
  const { token, user } = useSelector((state) => state.auth);
  const location = useLocation();
  const hideOnCall = String(location.pathname || '').startsWith('/call');
  const [digest, setDigest] = useState(null);
  const [open, setOpen] = useState(false);
  const [acking, setAcking] = useState(false);
  const [blastKey, setBlastKey] = useState(0);
  const [newsIndex, setNewsIndex] = useState(0);
  const [newsHovered, setNewsHovered] = useState(false);
  const [hideNews, setHideNews] = useState(() => readDigestHideNews());
  const lastAutoOpenRef = useRef(0);
  const electronForegroundRef = useRef(true);

  const localGreetingBits = useCallback(() => {
    const nightOwl = isNightOwlHour();
    return {
      nightOwl,
      greeting: greetingForLocalHour(),
      firstName: String(user?.firstName || user?.name || '').trim() || 'there',
      dateLabel: formatTodayDigestLabel(),
    };
  }, [user?.firstName, user?.name]);

  const openDigest = useCallback((data) => {
    const local = localGreetingBits();
    setDigest({
      ...(data || {}),
      nightOwl: local.nightOwl,
      greeting: local.greeting,
      firstName: data?.firstName || local.firstName,
      dateLabel: local.dateLabel,
    });
    setOpen(true);
    setBlastKey((key) => key + 1);
    lastAutoOpenRef.current = Date.now();
    markDigestShownTodayIst();
    touchLastActiveAt();
  }, [localGreetingBits]);

  const loadDigest = useCallback(async ({ forceOpen = false } = {}) => {
    if (!token) return;
    try {
      const data = await fetchMorningDigest();
      if (forceOpen) {
        openDigest(data || {});
        return;
      }
      if (data?.pending && !hasDigestComeTodayIst()) {
        openDigest(data);
        return;
      }
      setDigest(data || null);
    } catch (err) {
      console.error('Failed to load morning digest:', err);
      if (forceOpen) openDigest({});
    }
  }, [token, openDigest]);

  useEffect(() => {
    if (!token || hideOnCall) return undefined;
    loadDigest({ forceOpen: shouldAutoOpenDigest() });
    return undefined;
  }, [token, hideOnCall, loadDigest]);

  useEffect(() => {
    if (!token || hideOnCall) return undefined;
    const persistActive = () => {
      if (document.visibilityState === 'hidden') return;
      if (window.workloopRealm?.onWindowLifecycle && !electronForegroundRef.current) return;
      touchLastActiveAt();
    };
    const tryAutoOpen = () => {
      if (document.visibilityState === 'hidden') {
        touchLastActiveAt();
        return;
      }
      if (!shouldAutoOpenDigest()) {
        touchLastActiveAt();
        return;
      }
      if (Date.now() - lastAutoOpenRef.current < 60_000) return;
      lastAutoOpenRef.current = Date.now();
      loadDigest({ forceOpen: true });
    };
    persistActive();
    const heartbeat = window.setInterval(persistActive, 60_000);
    const untilTenAm = msUntilTenAmIst();
    const tenAmTimer = untilTenAm == null
      ? null
      : window.setTimeout(() => {
        if (!hasDigestComeTodayIst()) loadDigest({ forceOpen: true });
      }, Math.max(0, untilTenAm));
    document.addEventListener('visibilitychange', tryAutoOpen);
    window.addEventListener('focus', tryAutoOpen);
    window.addEventListener('pagehide', touchLastActiveAt);
    const unsubLifecycle = window.workloopRealm?.onWindowLifecycle?.((payload) => {
      if (payload?.state === 'background') {
        electronForegroundRef.current = false;
        touchLastActiveAt();
        return;
      }
      if (payload?.state === 'foreground') {
        electronForegroundRef.current = true;
        tryAutoOpen();
      }
    });
    return () => {
      window.clearInterval(heartbeat);
      if (tenAmTimer != null) window.clearTimeout(tenAmTimer);
      document.removeEventListener('visibilitychange', tryAutoOpen);
      window.removeEventListener('focus', tryAutoOpen);
      window.removeEventListener('pagehide', touchLastActiveAt);
      if (typeof unsubLifecycle === 'function') unsubLifecycle();
    };
  }, [token, hideOnCall, loadDigest]);

  useEffect(() => {
    const onOpen = () => {
      loadDigest({ forceOpen: true });
    };
    window.addEventListener('workloop:open-morning-digest', onOpen);
    return () => window.removeEventListener('workloop:open-morning-digest', onOpen);
  }, [loadDigest]);

  const handleClose = async () => {
    if (acking) return;
    const shouldAck = Boolean(digest?.pending);
    setAcking(true);
    try {
      if (shouldAck) {
        await acknowledgeMorningDigest();
        setDigest((prev) => (prev ? { ...prev, pending: false } : prev));
      }
      setOpen(false);
    } catch (err) {
      console.error('Failed to acknowledge morning digest:', err);
      setOpen(false);
    } finally {
      setAcking(false);
    }
  };

  const todayNews = useMemo(
    () => (Array.isArray(digest?.todayNews) ? digest.todayNews.filter((item) => item?.title && item?.url) : []),
    [digest],
  );

  useEffect(() => {
    setNewsIndex(0);
    setNewsHovered(false);
  }, [open, digest]);

  const daySchedule = useMemo(() => {
    const minutes = (label) => {
      const raw = String(label || '').trim();
      if (/^all day$/i.test(raw)) return -1;
      const match = raw.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (!match) return 24 * 60 + 1;
      let hour = Number(match[1]);
      const minute = Number(match[2]);
      const ampm = String(match[3] || '').toUpperCase();
      if (ampm === 'PM' && hour < 12) hour += 12;
      if (ampm === 'AM' && hour === 12) hour = 0;
      return hour * 60 + minute;
    };
    const meetings = Array.isArray(digest?.meetings) ? digest.meetings : [];
    const events = Array.isArray(digest?.events) ? digest.events : [];
    const reminders = Array.isArray(digest?.reminders) ? digest.reminders : [];
    const toItem = (item, kind) => ({
      id: `${kind}-${item.id || item.name}`,
      timeLabel: item.timeLabel || 'All day',
      name: item.name,
      kind,
      sort: minutes(item.timeLabel),
    });
    return [
      ...meetings.map((item) => toItem(item, 'call')),
      ...events.map((item) => toItem(item, 'event')),
      ...reminders.map((item) => toItem(item, 'reminder')),
    ].sort((a, b) => a.sort - b.sort || String(a.name).localeCompare(String(b.name)));
  }, [digest]);

  useEffect(() => {
    if (!open || hideNews || newsHovered || todayNews.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setNewsIndex((current) => (current + 1) % todayNews.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [open, hideNews, newsHovered, todayNews.length]);

  const ownBirthday = Boolean(digest?.ownBirthday);
  const blastPieces = useMemo(() => {
    if (!ownBirthday) return [];
    return [
      ...buildCrackerBurst('18%', '22%', blastKey + 3),
      ...buildCrackerBurst('82%', '22%', blastKey + 11),
    ];
  }, [ownBirthday, blastKey]);

  if (hideOnCall || !token || !open || !digest) return null;

  const birthdaysToday = Array.isArray(digest.birthdaysToday) ? digest.birthdaysToday : [];
  const birthdayLabel = (item) => item.name;
  const activeNews = todayNews[newsIndex] || todayNews[0];

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[2025] flex items-center justify-center p-4 sm:p-6 announcement-popup-root">
      <div
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-[2px] announcement-popup-backdrop"
        aria-hidden="true"
      />
      <div
        className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${
          ownBirthday ? 'from-rose-400/40 via-amber-300/25 to-fuchsia-400/30' : 'from-amber-400/30 via-orange-300/20 to-sky-400/30'
        } announcement-popup-wash`}
        aria-hidden="true"
      />

      {ownBirthday ? (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <CrackerIcon className="birthday-cracker birthday-cracker-left" />
          <CrackerIcon className="birthday-cracker birthday-cracker-right" />
          {blastPieces.map((piece) => (
            <span
              key={`${blastKey}-${piece.id}`}
              className={`announcement-blast-piece announcement-blast-shape-${piece.shape}`}
              style={{
                '--tx': `${piece.tx}px`,
                '--ty': `${piece.ty}px`,
                '--rot': `${piece.rot}deg`,
                '--size': `${piece.size}px`,
                '--delay': `${piece.delay}s`,
                '--duration': `${piece.duration}s`,
                backgroundColor: piece.color,
                left: piece.left,
                top: piece.top,
              }}
            />
          ))}
        </div>
      ) : null}

      <div
        className={`relative w-full max-w-xl announcement-popup-card ring-2 ${
          ownBirthday ? 'ring-rose-400/60' : 'ring-amber-400/50'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="morning-digest-title"
      >
        <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-white/40 via-transparent to-white/10 pointer-events-none" />
        <div className="relative rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
          <div className={`h-2 w-full bg-gradient-to-r ${
            ownBirthday ? 'from-rose-500 via-amber-300 to-fuchsia-500' : 'from-rose-400 via-amber-300 to-sky-400'
          }`} />
          <div className="p-6 sm:p-8">
            <div className="flex items-start gap-4 mb-5">
              <div className={`announcement-popup-badge flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${
                ownBirthday
                  ? 'bg-gradient-to-br from-rose-500 via-amber-300 to-fuchsia-500'
                  : 'bg-gradient-to-br from-amber-400 via-orange-300 to-sky-400'
              }`}>
                {ownBirthday ? (
                  <span className="text-2xl" aria-hidden="true">🎂</span>
                ) : (
                  <svg className="w-7 h-7 text-white drop-shadow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <span className={`inline-flex px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded-full mb-2 ${
                  ownBirthday
                    ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200'
                }`}>
                  {ownBirthday ? 'Birthday' : 'Today'}
                </span>
                <h3
                  id="morning-digest-title"
                  className={`text-2xl sm:text-3xl font-bold leading-tight tracking-tight ${
                    ownBirthday
                      ? 'birthday-title-shine bg-gradient-to-r from-rose-500 via-amber-500 to-fuchsia-500 bg-clip-text text-transparent'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  {ownBirthday
                    ? `Happy Birthday, ${digest.firstName}!`
                    : digest.nightOwl
                      ? (digest.greeting || greetingForLocalHour())
                      : `${digest.greeting || greetingForLocalHour()} ${digest.firstName}`}
                </h3>
                <p className="mt-1 text-base font-medium text-gray-600 dark:text-gray-300">
                  {ownBirthday
                    ? `It's your day — today is ${digest.dateLabel || formatTodayDigestLabel()}`
                    : `Today is ${digest.dateLabel || formatTodayDigestLabel()}`}
                </p>
              </div>
            </div>

            <div className="mb-7 max-h-[28rem] overflow-y-auto space-y-4">
              <label className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 dark:bg-gray-800/70 px-4 py-3 border border-slate-100 dark:border-gray-700">
                <span>
                  <span className="block text-sm font-bold text-gray-900 dark:text-white">Hide news</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">Show only today’s calls, events, and reminders</span>
                </span>
                <input
                  type="checkbox"
                  checked={hideNews}
                  onChange={(event) => {
                    const next = event.target.checked;
                    setHideNews(next);
                    writeDigestHideNews(next);
                  }}
                  className="h-5 w-5 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                />
              </label>
              {birthdaysToday.length > 0 && (
                <section className="rounded-xl bg-slate-50 dark:bg-gray-800/70 px-5 py-4 border border-slate-100 dark:border-gray-700">
                  <p className="text-xs font-bold uppercase tracking-wider text-rose-600 dark:text-rose-300 mb-2">
                    Birthdays today
                  </p>
                  <p className="text-gray-800 dark:text-gray-100 leading-7 text-base font-medium">
                    {ownBirthday
                      ? `Today is also ${joinList(birthdaysToday, birthdayLabel)}'s birthday`
                      : `Today is ${joinList(birthdaysToday, birthdayLabel)}'s birthday`}
                  </p>
                </section>
              )}

              {!hideNews && todayNews.length > 0 && activeNews ? (
                <section
                  className="rounded-xl bg-slate-50 dark:bg-gray-800/70 px-5 py-4 border border-slate-100 dark:border-gray-700"
                  onMouseEnter={() => setNewsHovered(true)}
                  onMouseLeave={() => setNewsHovered(false)}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-300">
                      Today&apos;s news
                    </p>
                    {todayNews.length > 1 ? (
                      <p className="text-[11px] font-semibold tabular-nums text-gray-400">
                        {newsIndex + 1}/{todayNews.length}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => openNewsLink(activeNews.url)}
                    className="w-full min-h-[4.5rem] text-left text-gray-800 dark:text-gray-100 hover:text-primary-700 dark:hover:text-primary-300 no-underline"
                  >
                    <span className="block pb-3 text-lg sm:text-xl font-semibold leading-7 no-underline">
                      {activeNews.title}
                    </span>
                    {(activeNews.source || activeNews.fromAdmin) ? (
                      <span className="mb-3 block text-sm font-semibold text-gray-400 dark:text-gray-500 no-underline">
                        {activeNews.fromAdmin ? (activeNews.source || 'Workloop') : activeNews.source}
                      </span>
                    ) : null}
                    <span className="mt-1 block h-px w-full bg-indigo-300 dark:bg-indigo-500/70" aria-hidden="true" />
                  </button>
                  {todayNews.length > 1 ? (
                    <div className="mt-3 flex items-center justify-center gap-1.5">
                      {todayNews.map((item, index) => (
                        <button
                          key={item.id || item.url || index}
                          type="button"
                          aria-label={`Show news ${index + 1}`}
                          onClick={() => setNewsIndex(index)}
                          className={`h-1.5 rounded-full transition-all ${
                            index === newsIndex
                              ? 'w-5 bg-indigo-500'
                              : 'w-1.5 bg-gray-300 dark:bg-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                  ) : null}
                </section>
              ) : null}

              <section className="rounded-xl bg-slate-50 dark:bg-gray-800/70 px-5 py-4 border border-slate-100 dark:border-gray-700">
                <p className="text-xs font-bold uppercase tracking-wider text-sky-600 dark:text-sky-300 mb-2">
                  Today&apos;s schedule
                </p>
                {daySchedule.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No calls, events, or reminders for today.</p>
                ) : (
                  <ul className="space-y-2">
                    {daySchedule.map((item) => (
                      <li key={item.id} className="flex items-start gap-3 text-gray-800 dark:text-gray-100">
                        <span className={`flex-shrink-0 w-20 text-sm font-bold tabular-nums ${
                          item.kind === 'call'
                            ? 'text-sky-700 dark:text-sky-300'
                            : item.kind === 'event'
                              ? 'text-violet-700 dark:text-violet-300'
                              : 'text-amber-700 dark:text-amber-300'
                        }`}>
                          {item.timeLabel}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-base font-medium leading-snug">{item.name}</span>
                          <span className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                            {item.kind === 'call' ? 'Call' : item.kind === 'event' ? 'Event' : 'Reminder'}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {birthdaysToday.length === 0 && daySchedule.length === 0 && (hideNews || todayNews.length === 0) && !ownBirthday && (
                <p className="rounded-xl bg-slate-50 dark:bg-gray-800/70 px-5 py-4 border border-slate-100 dark:border-gray-700 text-gray-700 dark:text-gray-200 text-base">
                  Have a focused day. We will show birthdays, calls, events, and reminders here as they come in.
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={acking}
                className={`announcement-ack-btn inline-flex items-center gap-2 px-6 py-3 text-base font-bold text-white rounded-xl shadow-lg disabled:opacity-60 ${
                  ownBirthday ? 'bg-rose-500 hover:bg-rose-600' : 'bg-amber-500 hover:bg-amber-600'
                }`}
              >
                {acking ? 'Saving…' : ownBirthday ? 'Thank you' : 'Have a great day'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MorningDigestPopup;
